import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import services from "../../services/services";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from "recharts";
import DateComponent from "../../component/date/DateComponent";

const COLORS = {
  ios: "#0A84FF",
  android: "#34C759",
  bokun: "#FF9500",
  viator: "#AF52DE",
  getYourGuide: "#FF2D55",
  website: "#5AC8FA",
  slices: ["#44C7B3", "#0A84FF", "#34C759", "#FF9500", "#AF52DE", "#FF2D55", "#5AC8FA", "#FFCC00", "#8E8E93", "#BF5AF2", "#64D2FF"],
};

const PLATFORM_COLOR: Record<string, string> = {
  iOS: COLORS.ios,
  Android: COLORS.android,
  Bokun: COLORS.bokun,
  Viator: COLORS.viator,
  GetYourGuide: COLORS.getYourGuide,
  Website: COLORS.website,
};

type Preset = "today" | "this_month" | "last_30" | "last_90" | "ytd" | "all";

// Dashboard days are Indian Standard Time (UTC+5:30) — presets compute IST
// dates regardless of the viewer's machine timezone, matching the backend.
const istNow = () => moment().utcOffset(330);

// Pie tooltip showing count, share, and USD revenue for platform/language slices.
const SplitTooltip = ({ active, payload, total }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const name = d.platform || d.language || d.name;
  const share = total > 0 ? ` · ${Math.round((d.count / total) * 100)}%` : "";
  return (
    <div className="bg-white border border-aqua/40 rounded-lg px-3 py-2 text-sm shadow-md">
      <p className="font-roboto font-bold text-dimblack">{name}</p>
      <p className="text-dimblack">{d.count} purchase{d.count === 1 ? "" : "s"}{share}</p>
      <p className="font-roboto font-bold text-aqua">${Number(d.revenue || 0).toFixed(2)} revenue</p>
    </div>
  );
};

const presetRange = (preset: Preset): { start: string; end: string } => {
  const today = istNow().format("YYYY-MM-DD");
  switch (preset) {
    case "today":
      return { start: today, end: today };
    case "this_month":
      return { start: istNow().startOf("month").format("YYYY-MM-DD"), end: today };
    case "last_30":
      return { start: istNow().subtract(29, "days").format("YYYY-MM-DD"), end: today };
    case "last_90":
      return { start: istNow().subtract(89, "days").format("YYYY-MM-DD"), end: today };
    case "ytd":
      return { start: istNow().startOf("year").format("YYYY-MM-DD"), end: today };
    case "all":
      return { start: "2020-01-01", end: today };
  }
};

const StatCard = ({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
}) => (
  <div className="flex-1 min-w-[180px] bg-white border border-aqua/40 rounded-2xl p-5 shadow-sm">
    <p className="text-xs font-openSans uppercase tracking-wider text-lightgray">{label}</p>
    <p className="text-3xl font-roboto font-bold text-dimblack mt-1" style={accent ? { color: accent } : undefined}>
      {value}
    </p>
    {sub && <p className="text-xs font-openSans text-lightgray mt-1">{sub}</p>}
  </div>
);

const PresetButton = ({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-openSans transition ${
      active ? "bg-aqua text-white" : "bg-white border border-gray-300 text-gray-700 hover:border-aqua"
    }`}
  >
    {label}
  </button>
);

const Dashboard = () => {
  const { adminPermission } = useSelector((state: any) => state.counter);

  const defaultRange = presetRange("this_month");
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [activePreset, setActivePreset] = useState<Preset | null>("this_month");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  const loadData = async (start: string, end: string) => {
    if (start && end && start > end) {
      toast.dismiss();
      toast.error("Start date must be on or before the end date");
      return;
    }
    setLoading(true);
    try {
      const res: any = await services.getDashboardStats({ startDate: start, endDate: end });
      setData(res?.data?.data || null);
    } catch (err) {
      console.error("Dashboard fetch failed", err);
      toast.dismiss();
      toast.error("Dashboard failed to load — try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(startDate, endDate);
  }, [startDate, endDate]);

  const applyPreset = (p: Preset) => {
    const r = presetRange(p);
    setActivePreset(p);
    setStartDate(r.start);
    setEndDate(r.end);
  };

  const stats = data?.stats || {
    total: 0,
    iosCount: 0,
    androidCount: 0,
    bokunCount: 0,
    viatorCount: 0,
    getYourGuideCount: 0,
    websiteCount: 0,
    newUsers: 0,
    revenue: 0,
    iapRevenue: 0,
    bookingRevenue: 0,
  };
  const platformSplit = data?.platformSplit || [];
  const languageSplit = data?.languageSplit || [];
  const topTours = data?.topTours || [];
  const topCities = data?.topCities || [];
  const daily = data?.daily || [];

  const pct = (n: number) => (stats.total > 0 ? Math.round((n / stats.total) * 100) : 0);

  // Slice top 10 + "Other" roll-up for the pie — if there are exactly 10, no rollup needed.
  const tourPieData = useMemo(() => topTours.slice(0, 10).map((t: any) => ({ name: t.name || t.productId, value: t.count })), [topTours]);

  if (adminPermission?.is_admin == 2 && !adminPermission?.dashboard) {
    return (
      <h1 className="text-3xl font-openSans font-bold text-aqua items-center flex justify-center h-[80vh]">
        You don't have permission
      </h1>
    );
  }

  return (
    <div className="w-full px-12 py-10">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h4 className="font-sans font-bold text-2xl text-dimblack">Overview</h4>
          <p className="text-sm text-lightgray mt-1">
            All sources · {moment(startDate).format("DD MMM")} – {moment(endDate).format("DD MMM YYYY")} · IST days
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PresetButton active={activePreset === "today"} onClick={() => applyPreset("today")} label="Today" />
          <PresetButton active={activePreset === "this_month"} onClick={() => applyPreset("this_month")} label="This Month" />
          <PresetButton active={activePreset === "last_30"} onClick={() => applyPreset("last_30")} label="30D" />
          <PresetButton active={activePreset === "last_90"} onClick={() => applyPreset("last_90")} label="90D" />
          <PresetButton active={activePreset === "ytd"} onClick={() => applyPreset("ytd")} label="YTD" />
          <PresetButton active={activePreset === "all"} onClick={() => applyPreset("all")} label="All Time" />
          <div className="ml-2 flex items-center">
            <DateComponent
              selecetedStartDate={(d: string) => {
                setActivePreset(null);
                setStartDate(d);
              }}
              selectedEndDate={(d: string) => {
                setActivePreset(null);
                setEndDate(d);
              }}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="flex flex-wrap gap-4 mt-6">
        <StatCard label="Total" value={stats.total.toLocaleString()} sub="All sources combined" />
        <StatCard label="iOS" value={stats.iosCount.toLocaleString()} sub={`${pct(stats.iosCount)}% of total`} accent={COLORS.ios} />
        <StatCard label="Android" value={stats.androidCount.toLocaleString()} sub={`${pct(stats.androidCount)}% of total`} accent={COLORS.android} />
        <StatCard label="Bokun" value={stats.bokunCount.toLocaleString()} sub={`${pct(stats.bokunCount)}% of total`} accent={COLORS.bokun} />
        <StatCard label="Viator" value={stats.viatorCount.toLocaleString()} sub={`${pct(stats.viatorCount)}% of total`} accent={COLORS.viator} />
        <StatCard label="GetYourGuide" value={stats.getYourGuideCount.toLocaleString()} sub={`${pct(stats.getYourGuideCount)}% of total`} accent={COLORS.getYourGuide} />
        <StatCard label="Website" value={stats.websiteCount.toLocaleString()} sub={`${pct(stats.websiteCount)}% of total`} accent={COLORS.website} />
        <StatCard label="New Users" value={stats.newUsers.toLocaleString()} />
        <StatCard
          label="Revenue (tracked)"
          value={`$${Number(stats.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`IAP $${Number(stats.iapRevenue || 0).toFixed(2)} + Bookings $${Number(stats.bookingRevenue || 0).toFixed(2)} · USD${(() => {
            const u = (stats.untrackedPayments || 0) + (stats.untrackedBookings || 0);
            return u > 0 ? ` · ${u} ${u === 1 ? "purchase has" : "purchases have"} no USD value` : "";
          })()}`}
        />
      </div>

      {loading && (
        <div className="flex w-full justify-center mt-8">
          <p className="text-aqua text-lg">Loading…</p>
        </div>
      )}

      {/* Chart grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">
        {/* Platform pie */}
        <div className="bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Platform Split</h5>
          {platformSplit.some((p: any) => p.count > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={platformSplit}
                  dataKey="count"
                  nameKey="platform"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(e: any) => `${e.platform}: ${e.count}`}
                >
                  {platformSplit.map((entry: any, idx: number) => (
                    <Cell
                      key={idx}
                      fill={PLATFORM_COLOR[entry.platform] || COLORS.slices[idx % COLORS.slices.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<SplitTooltip total={platformSplit.reduce((a: number, p: any) => a + (p.count || 0), 0)} />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-lightgray py-12">No purchases in this range</p>
          )}
        </div>

        {/* Top tours pie */}
        <div className="bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Most Purchased Tours (Top 10)</h5>
          {tourPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={tourPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                >
                  {tourPieData.map((_: any, idx: number) => (
                    <Cell key={idx} fill={COLORS.slices[idx % COLORS.slices.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-lightgray py-12">No tour purchases in this range</p>
          )}
        </div>

        {/* Language split (bookings carry the customer language; app purchases don't) */}
        <div className="bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Bookings by Language</h5>
          {languageSplit.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={languageSplit}
                  dataKey="count"
                  nameKey="language"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(e: any) => `${e.language}: ${e.count}`}
                >
                  {languageSplit.map((_: any, idx: number) => (
                    <Cell key={idx} fill={COLORS.slices[idx % COLORS.slices.length]} />
                  ))}
                </Pie>
                <Tooltip content={<SplitTooltip total={languageSplit.reduce((a: number, l: any) => a + (l.count || 0), 0)} />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-lightgray py-12">No bookings in this range</p>
          )}
        </div>

        {/* Daily time-series */}
        <div className="xl:col-span-2 bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Purchases Over Time</h5>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={daily} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => moment(v).format("DD MMM")}
                  minTickGap={24}
                  stroke="#999"
                  fontSize={11}
                />
                <YAxis allowDecimals={false} stroke="#999" fontSize={11} />
                <Tooltip labelFormatter={(v) => moment(v).format("DD MMM YYYY")} />
                <Legend />
                <Line type="monotone" dataKey="ios" name="iOS" stroke={COLORS.ios} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="android" name="Android" stroke={COLORS.android} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="bokun" name="Bokun" stroke={COLORS.bokun} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="viator" name="Viator" stroke={COLORS.viator} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="getYourGuide" name="GetYourGuide" stroke={COLORS.getYourGuide} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="website" name="Website" stroke={COLORS.website} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-lightgray py-12">No data in this range</p>
          )}
        </div>

        {/* Top cities bar */}
        <div className="xl:col-span-2 bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Top Cities by Purchases</h5>
          {topCities.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(60 + topCities.length * 40, 280)}>
              <BarChart data={topCities} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" allowDecimals={false} stroke="#999" fontSize={11} />
                <YAxis type="category" dataKey="name" width={160} stroke="#555" fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="#44C7B3" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-lightgray py-12">No city data in this range</p>
          )}
        </div>
      </div>

      {/* Top tours table */}
      {topTours.length > 0 && (
        <div className="mt-8 bg-white border border-aqua/30 rounded-2xl p-6 shadow-sm">
          <h5 className="font-roboto font-bold text-lg text-dimblack mb-4">Top Tours — Detail</h5>
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-Roboto text-lightgray uppercase">
              <tr>
                <th className="p-2">#</th>
                <th className="p-2">Tour / Bundle</th>
                <th className="p-2">Type</th>
                <th className="p-2">Purchases</th>
                <th className="p-2">Tracked Revenue (USD)</th>
              </tr>
            </thead>
            <tbody>
              {topTours.map((t: any, idx: number) => (
                <tr key={t.productId} className="border-t border-aqua/20">
                  <td className="p-2 text-lightgray">{idx + 1}</td>
                  <td className="p-2">{t.name || t.productId}</td>
                  <td className="p-2 capitalize">{t.type}</td>
                  <td className="p-2">{t.count}</td>
                  <td className="p-2">${Number(t.revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
